import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useUser } from '@/hooks/queries/auth';
import userService from '@/api/services/user';
import type { UserDetails } from '@/types';
import type { GroupUserRole } from '@/types/groups';
import { GROUP_USER_ROLE_OPTIONS } from '@/lib/groupRoles';
import { Loader, UserPlus } from 'lucide-react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import z from 'zod';
import { useQuery } from '@tanstack/react-query';
import {
  getUsersByGroupQueryKey,
  useGetAllUsersByGroupOptions,
  useGetUsersByGroupOptions,
} from '@/hooks/queries/groups';
import { getQueryClient } from '@/integrations/tanstack-query/RootProvider';
import UserItem from '@/components/folder/folder-details-dialog/UserView';

type InviteUserByEmailProps = {
  value: string;
  onValueChange: (value: string) => void;
  groupId?: string;
  onKnownUser: (user: UserDetails) => Promise<void> | void;
  inviteRole: GroupUserRole;
  onInviteRoleChange: (role: GroupUserRole) => void;
  buttonClassName?: string;
  folderId?: string;
  disabled?: boolean;
  /** Overrides admin check for showing the add-member action on suggestions. */
  canAddMembersToGroup?: boolean;
};

const emailValidation = (email: string) => z.string().email().safeParse(email).success;

export default function InviteUserByEmail({
  value,
  onValueChange,
  groupId,
  onKnownUser,
  inviteRole,
  onInviteRoleChange,
  buttonClassName,
  folderId,
  disabled = false,
  canAddMembersToGroup: canAddMembersToGroupProp,
}: InviteUserByEmailProps) {
  const [isProcessing, setIsProcessing] = useState(false);
  const loggedInUser = useUser((state) => state.user);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { queryClient } = getQueryClient();

  // Preload all users across groups for typeahead suggestions
  const { data: allGroupUsers } = useQuery(useGetAllUsersByGroupOptions(true));
  const { data: currentGroupMembers } = useQuery(
    useGetUsersByGroupOptions(String(groupId || ''), Boolean(groupId))
  );

  // Build a quick lookup for users already in the current group
  const currentGroupUserIdSet = useMemo(() => {
    const gid = String(groupId ?? '').trim();
    if (!gid) return new Set<string>();
    const entries = (allGroupUsers?.data ?? []) as Array<UserDetails & { groups?: Array<{ id: number }> }>;
    const set = new Set<string>();
    for (const u of entries) {
      if (u?.groups?.some?.((g) => String(g.id) === gid)) {
        set.add(String(u.id));
      }
    }
    return set;
  }, [allGroupUsers, groupId]);

  const suggestions = useMemo(() => {
    const q = value.trim().toLowerCase();
    if (!q) return [] as Array<UserDetails>;
    const entries = (allGroupUsers?.data ?? []) as Array<UserDetails & { groups?: Array<{ id: number }> }>;
    const seen = new Set<string>();
    const result: Array<UserDetails> = [];
    for (const user of entries) {
      const firstName = user.firstName?.toLowerCase() ?? '';
      const lastName = user.lastName?.toLowerCase() ?? '';
      const email = user.email?.toLowerCase() ?? '';
      const username = user.username?.toLowerCase?.() ?? '';
      if (
        firstName.includes(q) ||
        lastName.includes(q) ||
        email.includes(q) ||
        username.includes(q)
      ) {
        const key = String(user.id);
        if (!seen.has(key)) {
          seen.add(key);
          result.push(user);
        }
      }
      if (result.length >= 8) break;
    }
    return result;
  }, [allGroupUsers, value]);

  const handleInviteUser = useCallback(async () => {
    const email = value.trim();
    if (!emailValidation(email)) {
      toast.error('Invalid email format');
      return;
    }
    if (isProcessing || disabled) return;

    setIsProcessing(true);
    try {
      // Try to match against preloaded users first
      const preloadMatch = (allGroupUsers?.data as Array<UserDetails> | undefined)?.find(
        (user) => user.email?.toLowerCase()?.trim() === email.toLowerCase()
      );

      let foundUser = preloadMatch;
      // Fallback to API search by email for non-preloaded users
      if (!foundUser) {
        const searchRes = await userService.searchUserByEmail(email);
        foundUser = (searchRes?.data || []).find(
          (user) => user.email?.toLowerCase()?.trim() === email.toLowerCase()
        );
      }

      if (foundUser) {
        if (foundUser.id === Number(loggedInUser?.id)) {
          onValueChange('');
          toast.error('You cannot invite yourself');
          return;
        }

        await onKnownUser(foundUser);
        onValueChange('');
        return;
      }

      await userService.inviteUser({
        email,
        groupId: groupId && String(groupId).trim() ? Number(groupId) : undefined,
      });
      if (groupId) {
        await queryClient.invalidateQueries({
          queryKey: getUsersByGroupQueryKey(groupId),
        });
      }
      toast.success(`Invitation sent to ${email}`);
      onValueChange('');
    } catch (error) {
      const message = (error as Error)?.message || `Failed to process invite for ${email}`;
      toast.error(message);
    } finally {
      setIsProcessing(false);
    }
  }, [disabled, groupId, isProcessing, loggedInUser, onKnownUser, onValueChange, queryClient, value]);

  const handleEmailKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') handleInviteUser();
    },
    [handleInviteUser]
  );

  return (
    <div className="relative" ref={containerRef}>
      <div className="flex items-center gap-2">
        <Select
          value={inviteRole}
          onValueChange={(nextRole) => onInviteRoleChange(nextRole as GroupUserRole)}
          disabled={isProcessing || disabled}
        >
          <SelectTrigger size="sm" className="w-[110px] shrink-0" aria-label="Invite role">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {GROUP_USER_ROLE_OPTIONS.map(({ value: roleValue, label }) => (
              <SelectItem key={roleValue} value={roleValue}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          type="text"
          placeholder="Search or enter email to invite"
          value={value}
          containerClassName="py-0.5 px-1"
          onChange={(e) => onValueChange(e.target.value)}
          onKeyDown={handleEmailKeyPress}
          disabled={isProcessing || disabled}
          endIcon={
            <Button
              onClick={handleInviteUser}
              disabled={!value.trim() || isProcessing || disabled}
              className={buttonClassName ?? 'shrink-0'}
              variant="ghost"
            >
              {isProcessing ? (
                <>
                  <Loader className="mr-1 h-4 w-4 animate-spin" />
                  Working...
                </>
              ) : (
                <>
                  <UserPlus className="mr-1 h-4 w-4" />
                  Invite
                </>
              )}
            </Button>
          }
        />
      </div>

      {suggestions.length > 0 && (
        <div className="absolute z-20 mt-1 max-h-72 w-full overflow-y-auto rounded-md border bg-popover p-2 shadow-md">
          <div className="space-y-2">
            {suggestions.map((u) => {
              const alreadyInGroup = currentGroupUserIdSet.has(String(u.id));
              const isSelf = u.id === Number(loggedInUser?.id);
              const isSuggestionDisabled = alreadyInGroup || isSelf;
              const groupMemberRows = Array.isArray(currentGroupMembers?.data)
                ? currentGroupMembers.data
                : currentGroupMembers?.data?.members ?? [];
              const canAddMembersToGroup =
                canAddMembersToGroupProp ??
                groupMemberRows.some(
                  ({ user, isAdmin }) =>
                    String(user.id) === String(loggedInUser?.id) && isAdmin,
                );
              return (
                <UserItem
                  key={u.id}
                  user={u}
                  folderId={folderId ?? ''}
                  groupId={groupId ?? ''}
                  showMemberRoleControls={false}
                  showAddNewMember={!isSuggestionDisabled}
                  showLeaveButton={false}
                  showEditorRoleControls={false}
                  showBellIcon={false}
                  allowActions={true}
                  setQuery={onValueChange}
                  canAddMembersToGroup={canAddMembersToGroup}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
